import Skeleton from '@mui/material/Skeleton';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, ProjectSetupTranslationKeys } from 'intl/keys';
import { ShowConditionally } from 'components/common/ShowConditionally';
import { LabelledCard } from 'components/common/labelledCard/single';
import { useCommonSingleEntityViewStyles } from 'components/common/styles/useCommonSingleEntityViewStyles';
import { ButtonContained, ButtonIcon } from 'components/general';
import useIsMobile from 'hooks/useIsMobile';
import React, {
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { LabelledCardList } from 'components/common/labelledCard/list';
import { useSites } from 'store/site';

import { Archive, Edit, RestoreFromTrash } from '@mui/icons-material';
import {
  useCreateSite,
  withCreateSite,
} from 'components/dataCreationForms/site/withCreateSite';
import {
  useEditSite,
  withEditSite,
} from 'components/dataCreationForms/site/withEditSite';
import { useInspections } from 'store/inspection';
import ForbiddenTooltip from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import {
  createdAtSortAsc,
  groupAndSort,
  groupDeleted,
} from 'helpers/sort';
import { MemoCreateSiteWizardDialog } from 'views/site/wizard/create/dialog';
import { MemoEditSiteWizardDialog } from 'views/site/wizard/edit/dialog';

function _ProjectStructureView(): ReactElement {
  const isMobile = useIsMobile();
  const classes = useCommonSingleEntityViewStyles({ isMobile });
  const { openDialog: openEditSiteDialog } = useEditSite();
  const { openDialog: openCreateSiteDialog } = useCreateSite();

  const intl = useIntl();

  const { inspections, loading: loadingInspections } = useInspections();

  const {
    items: sites,
    loading: loadingSites,
    deleteSite,
    restoreSite,
  } = useSites();

  const inspectionsMap = useMemo(() => {
    const map = new Map<string, InspectionModel[]>();
    inspections.items.filter(keepUndeleted).forEach((inspection) => {
      inspection.sites.forEach((siteId) => {
        map.set(siteId, [...(map.get(siteId) || []), inspection]);
      });
    });
    return map;
  }, [inspections]);

  const loading = loadingSites || loadingInspections;

  const canDeleteSite = useCallback(
    (siteId: string) => {
      const inspections = inspectionsMap.get(siteId) || [];
      const canDelete = inspections.every(
        (inspection) => inspection.isCompleted
      );
      return {
        disabled: !canDelete,
        reason: ProjectSetupTranslationKeys.deleteConflictInspectionsInProgress,
      };
    },
    [inspectionsMap]
  );

  const cardItems = useMemo(() => {
    return sites
      .sort(groupAndSort(groupDeleted, createdAtSortAsc))
      .map((site) => {
        return {
          _id: site._id,
          label: site.label,
          redirectTo: `/site/${site._id}`,
          deleted: site.deleted,
          code: site.code,
        };
      });
  }, [sites]);

  const cardChildren = useMemo(() => {
    return cardItems.map((cardItem) => {
      const { disabled, reason } = canDeleteSite(cardItem._id);
      return (
        <LabelledCard
          key={cardItem._id}
          label={cardItem.label}
          redirectTo={cardItem.redirectTo}
          deleted={cardItem.deleted}
          EndIcon={
            <>
              <ButtonIcon
                data-qa={'project-structure-edit-site'}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openEditSiteDialog({ _id: cardItem._id });
                }}
              >
                <Edit />
              </ButtonIcon>
              <ShowConditionally isShown={!cardItem.deleted}>
                <ForbiddenTooltip visibleWhen={disabled} reason={reason}>
                  <ButtonIcon
                    data-qa={'project-structure-archive-site'}
                    disabled={disabled}
                    onClick={(e: MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (disabled) {
                        return;
                      }
                      deleteSite(cardItem);
                    }}
                  >
                    <Archive />
                  </ButtonIcon>
                </ForbiddenTooltip>
              </ShowConditionally>
              <ShowConditionally isShown={cardItem.deleted}>
                <ButtonIcon
                  onClick={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    restoreSite(cardItem);
                  }}
                >
                  <RestoreFromTrash />
                </ButtonIcon>
              </ShowConditionally>
            </>
          }
        />
      );
    });
  }, [
    cardItems,
    deleteSite,
    canDeleteSite,
    restoreSite,
    openEditSiteDialog,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.actionsContainer}>
        <h2>{formatTranslation(intl, CommonTranslationKeys.sites)}</h2>
        <ButtonContained
          data-qa={'project-structure-create-site'}
          onClick={() => openCreateSiteDialog({})}
        >
          {formatTranslation(intl, ProjectSetupTranslationKeys.createSite)}
        </ButtonContained>
      </div>

      {loading ? (
        <>
          <Skeleton variant='text' width={'90%'} height={56} />
          <Skeleton variant='text' width={'90%'} height={56} />
          <Skeleton variant='text' width={'90%'} height={56} />
        </>
      ) : (
        <LabelledCardList>{cardChildren}</LabelledCardList>
      )}

      <MemoCreateSiteWizardDialog />
      <MemoEditSiteWizardDialog />
    </div>
  );
}

export const ProjectStructureView = withCreateSite(
  withEditSite(_ProjectStructureView)
);
